import axios from 'axios';
import { useContext } from 'react';
import { toast } from 'sonner';
import { AuthContext } from '../../context/AuthContext';

// Create a separate refresh token axios instance to avoid interceptor loops
const refreshTokenAxios = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const useAxiosInstance = () => {
  const { login, logout } = useContext(AuthContext);

  const getAuthToken = () => localStorage.getItem('token');
  const getRefreshToken = () => localStorage.getItem('refreshToken');

  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor
  axiosInstance.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor
  let isRefreshing = false;
  let failedRequestsQueue = [];

  const processQueue = (error) => {
    failedRequestsQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(axiosInstance(prom.originalRequest));
      }
    });
    failedRequestsQueue = [];
  };

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      
      // Network error handling
      if (!error.response) {
        toast.error(error.request ? 'Network error' : 'Request error');
        return Promise.reject(error);
      }

      const { status, data } = error.response;
      
      // Handle 401 Unauthorized
      if (status === 401 && originalRequest && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({ resolve, reject, originalRequest });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = getRefreshToken();
          if (!refreshToken) {
            logout();
            throw new Error('No refresh token available');
          }

          // Use separate refresh token instance
          const { data } = await refreshTokenAxios.post('/users/refresh-token', { 
            refreshToken 
          });

          login(data.token, data.refreshToken, data.userId);
          
           // Clone request to prevent modifying the original
           const retryRequest = { ...originalRequest };

           retryRequest.headers = {
             ...originalRequest.headers,
             Authorization: `Bearer ${data.token}`, // Update token
           };
          
          // Retry original request
          const response = await axiosInstance(retryRequest);
          processQueue();
          return response;
        } catch (refreshError) {
          const refreshStatus = refreshError?.response?.status;

          // Logout ONLY if refresh token request itself fails with 401
          if (refreshStatus === 401) {
            processQueue(refreshError);
            logout();
            window.location.href = '/signin';
          } 

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      
      // Handle other status codes
      //const backendMessage = data?.error || 'An unexpected error occurred';
      // Handle errors
      const backendMessage = extractErrorMessage(data);

      // Handle only the errors that your backend currently processes
      if (status === 400 || status === 404) {
        toast.error(backendMessage); // Show actual backend error
      } else {
        // Default error message for other unhandled cases (like 403, 415, 500)
        toast.error(`Error (${status}): ${backendMessage}`);
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

function extractErrorMessage(responseData) {
  if (!responseData) return 'An unexpected error occurred';

  if (typeof responseData.error === 'string') {
    return responseData.error; // Direct string error message
  }

  if (responseData.errors) {
    // Extract errors from nested objects
    return Object.values(responseData.errors)
      .flat()
      .join(' | '); // Join multiple error messages
  }

  return 'An unexpected error occurred';
}

export default useAxiosInstance;